/**
 * Central state store with diff-based history, transaction API, and IndexedDB auto-save.
 * All state mutations go through this store to ensure undo/redo and persistence.
 */

import { History, applyPatches } from './history.js';
import { stripLocalOnlyPatches, isEditableIdRepairAbsentPatch } from '../shared/collab-protocol.js';
import {
  normalizeEditableIdRepairState,
  normalizeEditableIdRepairs,
  editableIdRepairSections
} from '../shared/editable-id-repair.js';
import { trimHistoryAgainst } from '../collab/op-transform.js';
import { AssetStore } from './asset-store.js';
import { eventBus } from './events.js';
import { createIdbHandle, withStore, promisifyRequest } from './idb.js';
import { reportError } from './report-error.js';
import { recordSessionBoundaryMetric } from './session-boundary-observability.js';
import { getFeatureDefaults } from '../export/feature-registry.js';
import { createDefaultClientInfo } from '../shared/client-info.js';
import { normalizeLogoSlots } from '../editing/logo-normalizer.js';
import { normalizeMotionHTML, normalizeMotionResidue } from '../shared/motion-residue.js';
import { DEFAULT_THEME } from '../shared/theme-tokens.js';

const IDB_NAME = 'pb-state';
const IDB_VERSION = 1;
const IDB_STORE = 'projects';
/**
 * The single global autosave slot every project shared until SB-3.
 *
 * It held whichever project was edited LAST, not the project a given tab
 * thought it had — so a tab restored by browser session-restore could come back
 * holding a completely different project, and the restore toast said "Restored
 * your last session" either way. Kept as the LEGACY key: SB-3a writes it as well
 * as the new one so a rollback to pre-SB-3 code still finds readable data, and
 * SB-3b stops writing it.
 */
const LEGACY_AUTOSAVE_KEY = 'autosave';
const AUTOSAVE_DELAY = 2000;

/**
 * Signed-out / dev principal. A stable literal rather than null so the key
 * grammar never produces `autosave:null:<id>` or a bare `autosave::<id>`.
 */
export const LOCAL_PRINCIPAL_SCOPE = 'local';

/**
 * Autosave slot key. Scoped by (principal, project) — BOTH halves are load
 * bearing:
 *  - project, because one slot for every project is what let a tab restore
 *    someone else's project;
 *  - principal, because a project-only key still leaks recovery across accounts
 *    on a shared machine: sign out, sign in as someone else, and the slot (plus
 *    the last-opened pointer) is still sitting there.
 */
export function autosaveKeyFor(principalScope, projectId) {
  return `autosave:${principalScope || LOCAL_PRINCIPAL_SCOPE}:${projectId}`;
}

/**
 * Write blockers (SB-0). Writability is the emptiness of the held set, never a
 * boolean — several of these overlap in practice (a deleted project in a
 * non-owner tab holds two), and with one flag the last gate to release would
 * silently re-enable writing on a project another gate still forbids.
 *
 * Declared HIGHEST PRIORITY FIRST: the order decides which reason a user-facing
 * message names when more than one is held.
 */
export const WRITE_BLOCKER = Object.freeze({
  /** Agent adoption is replacing the whole project state. Work is PRESERVED. */
  AGENT_ADOPTING: 'agent-adopting',
  /** Agent Before review is showing detached state. Work is PRESERVED. */
  AGENT_REVIEW: 'agent-review',
  /**
   * HR-2 — the History Rail is previewing a saved version on the canvas. Work is
   * PRESERVED, exactly like AGENT_REVIEW.
   *
   * A SECOND blocker rather than reusing AGENT_REVIEW, because a blocker is also
   * a sentence: `READ_ONLY_MESSAGES` maps the top-ranked held blocker to the
   * reason shown to the user, and "Switch back to After to keep editing" is the
   * wrong sentence for someone looking at v14 of their own history. The two
   * owners are mutually exclusive at the seam, so only one is ever held.
   */
  DETACHED_PREVIEW: 'detached-preview',
  /** The project is gone or inaccessible server-side. Work is PRESERVED. */
  PROJECT_UNAVAILABLE: 'project-unavailable',
  /** No usable session. */
  AUTH: 'auth',
  /** Another session of this account holds the editor claim (SB-4b). */
  SESSION_CLAIM: 'session-claim',
  /** Another tab in this browser owns the autosave slot. Work is DISCARDED. */
  TAB_GUARD: 'tab-guard'
});
const WRITE_BLOCKERS = Object.freeze(Object.values(WRITE_BLOCKER));

/**
 * A blocker is also a SENTENCE. SB-0's rule: read-only has more than one cause,
 * and naming the wrong one is worse than being vague.
 *
 * Moved here from `ui/shell.js` in HR-3 so it lives with the enum it describes
 * and has ONE home. The History Rail needs the same sentences and cannot import
 * the shell — the shell imports the rail, so that would close a cycle — and
 * duplicating six strings is how two surfaces start telling a user two different
 * things about one state.
 */
const READ_ONLY_MESSAGES = Object.freeze({
  [WRITE_BLOCKER.AGENT_REVIEW]: 'Switch back to After to keep editing this project.',
  [WRITE_BLOCKER.DETACHED_PREVIEW]:
    'You are viewing an older version. Press Escape to return to your work.',
  [WRITE_BLOCKER.PROJECT_UNAVAILABLE]:
    'This project is no longer available on the server, so it cannot be edited.',
  [WRITE_BLOCKER.AUTH]: 'Sign in again to keep editing this project.',
  [WRITE_BLOCKER.SESSION_CLAIM]:
    'This project is being edited in another window. Take over from the banner to edit here.',
  [WRITE_BLOCKER.TAB_GUARD]: 'This tab is read-only — another tab is editing this project.'
});

/** The honest sentence for a held blocker, or a vague one if it is unmapped. */
export function readOnlyReasonFor(blocker) {
  return READ_ONLY_MESSAGES[blocker] || 'This project is read-only right now.';
}
const VALID_BLOCKERS = new Set(WRITE_BLOCKERS);
const DEFAULT_BRAND_VOICE = Object.freeze({
  formality: null,
  contractions: null,
  sentenceLengthTarget: null,
  forbiddenWords: []
});
const DEFAULT_FLAGS = Object.freeze({
  agentReviewMode: false,
  aiRewrite: false,
  aiLogPrompts: true,
  aiQualityMode: false,
  renderDebug: false,
  doubleBuffer: false,
  srcdocMode: false,
  offThreadAssets: false
});
// Canonical values live in shared/site-effects.js (worker-safe single source,
// crm-bridge B2); re-exported here so existing importers are unchanged.
import { SITE_EFFECT_DEFAULTS } from '../shared/site-effects.js';
export { SITE_EFFECT_DEFAULTS };
// Entrance animations (EA-1). Same doctrine as SITE_EFFECT_DEFAULTS: the
// canonical record lives in the pure, worker-safe module and this file only
// consumes it, so the default a new project gets and the default the collab
// room re-materializes cannot drift.
import { ENTRANCE_DEFAULTS, normalizeEntranceState } from '../shared/entrance-presets.js';
import { normalizeScheduleState } from '../shared/schedule-state.js';

export function getByPath(obj, path) {
  const keys = path.split('.');
  let current = obj;
  for (const key of keys) {
    if (current == null) return undefined;
    current = current[key];
  }
  return current;
}

export function setByPath(obj, path, value) {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (current[keys[i]] == null) current[keys[i]] = {};
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
}

/** Segment-wise path intersection — one path is the other or its prefix. */
function pathsIntersectSegments(pathA, pathB) {
  const a = pathA.split('.');
  const b = pathB.split('.');
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Coalesce a transaction's raw patch stream at commit (16B1). A 2s scrub is
 * ~120 frames of writes to the same path; committing the raw stream stores
 * every intermediate value in the history entry and ships it all to collab.
 *
 * Rules, in order of authority:
 *  - EXACTLY repeated paths merge to one patch: first oldValue, last newValue.
 *  - Raw order is preserved whenever ancestor/descendant paths intersect (the
 *    same segment-prefix rule the collab layer uses): a write to `overrides`
 *    seals any open `overrides.spacing.top` merge slot and vice versa, so a
 *    later same-path write appends a NEW patch instead of merging backward
 *    across the dependent write.
 *  - Net no-ops are dropped, so a scrub returned to its starting value
 *    contributes nothing — and an all-no-op transaction commits NOTHING: no
 *    history entry, no collab frame.
 */
function coalesceTransactionPatches(patches) {
  const out = [];
  const openByPath = new Map(); // path -> index in `out` that may still merge
  for (const patch of patches) {
    const openIndex = openByPath.get(patch.path);
    if (openIndex != null) {
      out[openIndex].newValue = patch.newValue;
      continue;
    }
    for (const [path] of openByPath) {
      if (path !== patch.path && pathsIntersectSegments(path, patch.path)) {
        openByPath.delete(path);
      }
    }
    openByPath.set(patch.path, out.length);
    out.push({ path: patch.path, oldValue: patch.oldValue, newValue: patch.newValue });
  }
  return out.filter((p) => !valuesEqual(p.oldValue, p.newValue));
}

function valuesEqual(a, b) {
  if (Object.is(a, b)) return true;
  if (typeof a !== typeof b) return false;
  if (a == null || b == null) return false;
  if (typeof a !== 'object') return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!valuesEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!valuesEqual(a[key], b[key])) return false;
  }
  return true;
}

/**
 * One parse, both DOM normalizers: logo slots and motion residue. Section
 * html/originalHtml are plain strings persisted verbatim through save / load /
 * zip / cloud / collab, so a project saved before the parser learned to strip
 * frozen animation state (Motion Restoration M3) still carries it — including
 * live carousel <script> tags — and reset-to-original would resurrect it from
 * originalHtml even after html was cleaned. Running the motion normalizer here,
 * on the write path nothing escapes, is the backstop; it is idempotent and
 * returns the input string byte-identical when nothing changed, so the
 * valuesEqual guard in update()/batch() still suppresses no-op history.
 */
function normalizeLogoHTML(html, motionContext = null) {
  if (typeof html !== 'string') return html;
  if (typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;
  const logoChanged = normalizeLogoSlots(root);
  // Site/sectionId gate the registry's scoped patterns; without them only the
  // marker-matched (unscoped) patterns run, which is the safe subset.
  const motionChanged = normalizeMotionResidue(root, {
    site: motionContext?.site || null,
    sectionId: motionContext?.sectionId || null
  }).changed;
  return logoChanged || motionChanged ? root.innerHTML : html;
}

function sectionMotionContext(section) {
  return {
    site: section?.templateSiteName || null,
    sectionId: section?.templateSectionId || null
  };
}

function sectionPathForHtmlField(path) {
  if (typeof path !== 'string') return null;
  if (!/\.(?:html|originalHtml)$/.test(path)) return null;
  return path.replace(/\.(?:html|originalHtml)$/, '');
}

function sectionMotionContextForPath(state, path) {
  const sectionPath = sectionPathForHtmlField(path);
  if (!sectionPath) return null;
  return sectionMotionContext(getByPath(state, sectionPath));
}

function normalizeSectionLogoSlots(section) {
  if (!section || typeof section !== 'object') return section;
  const context = sectionMotionContext(section);
  if (typeof section.html === 'string') {
    section.html = normalizeLogoHTML(section.html, context);
  }
  if (typeof section.originalHtml === 'string') {
    section.originalHtml = normalizeLogoHTML(section.originalHtml, context);
  }
  return section;
}

function normalizeSectionMotionOnly(section) {
  if (!section || typeof section !== 'object') return section;
  const context = sectionMotionContext(section);
  if (typeof section.html === 'string') {
    section.html = normalizeMotionHTML(section.html, context);
  }
  if (typeof section.originalHtml === 'string') {
    section.originalHtml = normalizeMotionHTML(section.originalHtml, context);
  }
  return section;
}

function normalizePageSections(page, normalizeSection) {
  if (!page || typeof page !== 'object' || !Array.isArray(page.sections)) return;
  for (const section of page.sections) normalizeSection(section);
}

function shouldNormalizeLogoPath(path) {
  if (typeof path !== 'string') return false;
  return (
    path === 'sharedSections' ||
    path.startsWith('sharedSections.') ||
    path === 'pages' ||
    path.includes('.sections') ||
    path.endsWith('.sections')
  );
}

function normalizeSectionValue(value, path, normalizeSection) {
  if (!shouldNormalizeLogoPath(path)) return value;
  if (Array.isArray(value)) {
    if (path === 'pages') {
      for (const page of value) normalizePageSections(page, normalizeSection);
    } else {
      for (const item of value) normalizeSection(item);
    }
    return value;
  }
  if (value && typeof value === 'object') {
    if ('html' in value || 'originalHtml' in value) {
      normalizeSection(value);
    } else {
      normalizePageSections(value, normalizeSection);
      if (value.navigation) normalizeSection(value.navigation);
      if (value.footer) normalizeSection(value.footer);
    }
  }
  return value;
}

function normalizeLogoSlotsInValue(value, path = '', stateForContext = null) {
  if (typeof value === 'string') {
    const context = sectionMotionContextForPath(stateForContext, path);
    return sectionPathForHtmlField(path) ? normalizeLogoHTML(value, context) : value;
  }
  return normalizeSectionValue(value, path, normalizeSectionLogoSlots);
}

function normalizeMotionOnlyInValue(value, path = '', stateForContext = null) {
  if (typeof value === 'string') {
    const context = sectionMotionContextForPath(stateForContext, path);
    return sectionPathForHtmlField(path) ? normalizeMotionHTML(value, context) : value;
  }
  return normalizeSectionValue(value, path, normalizeSectionMotionOnly);
}

function normalizeRemoteMotionPatches(state, patches) {
  if (!Array.isArray(patches) || patches.length === 0) return [];
  const cursor = structuredClone(state);
  const normalized = [];
  for (const patch of patches) {
    const next = structuredClone(patch);
    if (next.op === 'replace') {
      next.value = normalizeMotionOnlyInValue(next.value, next.path, cursor);
    } else if (next.op === 'insert') {
      next.item = normalizeMotionOnlyInValue(next.item, next.path, cursor);
    }
    normalized.push(next);
    try {
      applyPatches(cursor, [structuredClone(next)]);
    } catch {
      // The real apply path below reports malformed remote ops. The cursor is
      // only for deriving context for later patches, so stop trusting it.
    }
  }
  return normalized;
}

function normalizeStateLogoSlots(state) {
  if (!state || typeof state !== 'object') return;
  if (state.sharedSections?.navigation) normalizeSectionLogoSlots(state.sharedSections.navigation);
  if (state.sharedSections?.footer) normalizeSectionLogoSlots(state.sharedSections.footer);
  for (const page of state.pages || []) {
    for (const section of page.sections || []) {
      normalizeSectionLogoSlots(section);
    }
  }
}

export function createDefaultState() {
  const projectName = 'Untitled';
  const projectId = crypto.randomUUID();
  return {
    projectName,
    clientInfo: createDefaultClientInfo(projectName),
    theme: { ...DEFAULT_THEME },
    font: 'Roboto',
    headingFont: null,
    headingFontWeight: null,
    /**
     * WHERE the theme fonts came from: 'template' when a template load derived
     * them, 'user' once someone actually picked one, null on a blank project.
     *
     * The distinction is load-bearing, not bookkeeping. Section CSS rewrites
     * every `font-family` to `var(--pb-heading-font, <original>)` so the theme
     * can restyle a template — but on load the theme font IS one of the
     * template's own fonts, so that substitution replaces the template's
     * accent faces with its own base face and no user ever asked for it.
     * abshillbillygyros.com declares Raleway, Slackey and Rock Salt; Raleway
     * won all three. `css-assembler.js` skips the rewrite while this is
     * 'template'.
     */
    fontSource: null,
    fontHotlinkFallback: true,
    iconTinting: true,
    darkMode: false,
    siteEffects: { ...SITE_EFFECT_DEFAULTS },
    // Entrance animations (EA-1). `template` is whether the TEMPLATE's own
    // scroll-reveal convention stays on; `preset`/`stagger` are the builder's
    // own entrance, off by default so no existing project changes behaviour.
    entrance: { ...ENTRANCE_DEFAULTS },
    sharedSections: {
      navigation: null,
      footer: null
    },
    pages: [
      {
        id: 'page-1',
        name: 'Home',
        sections: [],
        seo: { title: '', description: '', ogImage: '', canonicalUrl: '' }
      }
    ],
    activePage: 'page-1',
    headCode: '',
    bodyCode: '',
    buttonStyles: null,
    formStyles: null,
    features: getFeatureDefaults(),
    // App identity for the exported PWA. Separate from `features` because these
    // are DATA, not flags: turning the install prompt off must not discard the
    // icon and screenshots the user picked.
    // Screenshots carry their own dimensions and MIME type, measured when the
    // user picks them — the manifest requires `sizes`/`type`, and the exporter
    // also runs under Node (golden-export-test) where nothing can measure a blob.
    pwaInstall: { appIcon: null, screenshots: [] },
    // ES-5. Contract templates authored in the Export Settings modal and seeded
    // into the exported site's contract_templates table. Data, not code: the
    // exported site owns the rows and can edit them, and the e-sign pack keeps
    // versioning independently of whatever a business's contract says.
    esignTemplates: [],
    flags: { ...DEFAULT_FLAGS },
    brandVoice: { ...DEFAULT_BRAND_VOICE, forbiddenWords: [] },
    deploy: { githubRepo: '', siteDomain: '', cmsAllowedUsers: [], analyticsWebsiteId: '' },
    sourceTemplateDomain: null,
    sourceClientProfile: null,
    // Skin from URL composition context: semantic client profile + source
    // profiles so sections added AFTER composing get re-skinned on insert
    // (shared/composition-skin.js). null = feature dormant.
    compositionContext: null,
    // P3 — durable "the operator decided this finding is fine" records, keyed by
    // a DIGEST of the acceptKey (setByPath splits on '.', so a raw key holding a
    // domain or selector would silently nest). Project data: it syncs to collab
    // peers and versions with the project, because an accept IS a project
    // decision with an author and a note.
    auditAccepts: {},
    projectId,
    identityRepairLocalOrigin: { version: 1, projectId },
    ownerSub: 'admin',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    schemaVersion: 8
  };
}

export class StateStore {
  constructor(options = {}) {
    this.state = null;
    this.listeners = new Set();
    this.history = new History();
    this.assets = new AssetStore();
    this._revision = 0;
    // Monotonic load/mutation epoch. Unlike _revision (which init() resets for
    // history semantics), _epoch only ever increases — bumped by every mutation
    // (_bumpRevision) AND every init(). It is the race-detection primitive for
    // "did anything change while I awaited an async load" (see getEpoch()).
    this._epoch = 0;
    this._autoSaveTimer = null;
    this._saveToken = 0;
    this._dirtyVersion = 0;
    this._dirty = false;
    // Write blockers (SB-0). Writability is the emptiness of this set, so there
    // is no second source of truth to drift from it.
    this._blockers = new Set();
    // CE-4: an OPTIONAL path-scoped write policy (core/write-policy.js) — a
    // second gate beside the blockers, never a replacement. Null = allow-all,
    // and the builder never installs one; only the client editor shell does.
    this._writePolicy = null;
    // SB-3a. `local` until the shell resolves auth — also the signed-out and
    // dev value, so the key grammar is never `autosave:null:<id>`.
    this._principalScope = LOCAL_PRINCIPAL_SCOPE;
    this._autosaveRetryDelays = [1000, 3000];
    this._txn = null;
    // Collab Phase 7: op-capture sink installed by collab/op-capture.js while
    // a live session is connected. Every committed history entry (and
    // undo/redo/amend) emits its wire copy here — with LOCAL_ONLY_PATHS
    // stripped. Null for solo sessions: zero behavior change.
    this._collabSink = null;
    this._identityRepairHistoryGuard = null;
    this._applyingRemote = false;
    // Injection seam: tests pass a scriptable fake IndexedDB factory. When
    // omitted the handle resolves globalThis.indexedDB lazily at open time.
    this._idbFactory = options.idbFactory || null;
    this._idb = null;
  }

  /** Install/remove the collab op-capture sink. */
  setCollabSink(fn) {
    this._collabSink = typeof fn === 'function' ? fn : null;
  }

  /** The session owns readiness; core refuses repair history before any write. */
  setIdentityRepairHistoryGuard(fn) {
    this._identityRepairHistoryGuard = typeof fn === 'function' ? fn : null;
  }

  _allowsIdentityRepairHistory(entry, direction) {
    if (
      !entry ||
      ![...entry.forward, ...entry.reverse].some((patch) =>
        patch.path?.split('.').includes('editableIdRepairs')
      )
    )
      return true;
    let verdict;
    try {
      verdict = this._identityRepairHistoryGuard?.(entry, direction);
    } catch (error) {
      reportError('Element identity repair', error);
      return false;
    }
    if (verdict?.ok === true) return true;
    reportError(
      'Element identity repair',
      new Error(
        verdict?.message ||
          'Wait for this project’s live session to finish syncing before undoing or redoing element identity repair.'
      )
    );
    return false;
  }

  _emitCollabOp(forward, reverse, label, source) {
    if (typeof this._collabSink !== 'function' || this._applyingRemote) return;
    // The wire copy strips local-only paths (activePage/updatedAt/
    // cloudVersion); an op left empty by stripping — e.g. a bare Switch
    // page — is NOT sent. The local history entry keeps its full patches
    // for undo fidelity.
    const wireForward = stripLocalOnlyPatches(forward);
    if (!wireForward.length) return;
    const wireReverse = stripLocalOnlyPatches(reverse);
    try {
      this._collabSink({
        forward: structuredClone(wireForward),
        reverse: structuredClone(wireReverse),
        label: label ?? null,
        source
      });
    } catch (err) {
      // A broken sink must never break editing.
      console.warn('[StateStore] collab sink failed:', err);
    }
  }

  /**
   * The ONE committed-history choke point (Collab Phase 7): every history
   * entry — the six internal mutation sites AND external compound-entry
   * builders (page-manager) via the public pushHistoryEntry — lands here so
   * op capture can never be bypassed.
   *
   * 16B1 (C3): patches are CLONED at this boundary. History.push stores its
   * arguments by reference, so a forward `replace` value used to alias the
   * live state object and a whole-array reverse aliased the live array — the
   * next in-place mutation silently corrupted redo/undo. Bulk override
   * objects (16B2) make that aliasing far more reachable than single-field
   * edits ever did; cloning here closes the class for every entry.
   */
  _pushHistory(forward, reverse, label, options = {}) {
    const forwardCopy = structuredClone(forward);
    const reverseCopy = structuredClone(reverse);
    this.history.push(forwardCopy, reverseCopy, label, options);
    this._emitCollabOp(forwardCopy, reverseCopy, label, 'mutation');
  }

  /**
   * PUBLIC sanctioned entry point for modules that build compound history
   * entries after mutating state directly (page-manager add/delete/rename).
   * Never call store.history.push directly — it would bypass op capture.
   */
  pushHistoryEntry(forward, reverse, label, options = {}) {
    // BC-1: writability, not just policy. A direct mutator in a read-only tab
    // (another tab owns the slot, a saved version is being previewed) used to
    // record its entry AND broadcast it as a collab op; only the autosave gate
    // noticed. Callers are expected to check `refuseIfReadOnly` BEFORE they
    // mutate — this is the depth behind that, and it undoes with the reverse
    // patches exactly like the policy tripwire below.
    if (!this.isWritable()) {
      try {
        applyPatches(this.state, structuredClone(reverse || []));
      } catch (err) {
        reportError('Read-only guard', err, { user: 'silent' });
      }
      this.notify();
      return this._rejectReadOnlyWrite(label || 'history entry');
    }
    // CE-4a tripwire. Direct mutators (page-manager, bulk-operations) mutate
    // the LIVE state from getState() and then record history here — a path the
    // mutator guards never see, so a check alone would be too late. Under a
    // policy the primary defense is the client entry's static graph (those
    // modules never load); this tripwire is depth: the entry hands us the
    // REVERSE patches, so the mutation is UNDONE, the entry refused, and the
    // rejection reported.
    if (this._writePolicy) {
      const structural = (forward || []).some((p) => p && p.op !== 'replace');
      const rejects =
        (structural && !this._writePolicy.allowsStructural) ||
        this._policyRejects(
          (forward || []).filter((p) => p?.op === 'replace').map((p) => p.path),
          label || 'history entry'
        );
      if (rejects) {
        if (structural) this._policyRejectsStructural(label || 'history entry');
        try {
          applyPatches(this.state, structuredClone(reverse || []));
        } catch (err) {
          reportError('Write policy', err, { user: 'silent' });
        }
        this.notify();
        return false;
      }
    }
    this._pushHistory(forward, reverse, label, options);
    return true;
  }

  /**
   * BC-1. For direct mutators (page-manager) that change live state and then
   * record history: call BEFORE the mutation. Returns true — and emits the
   * same `store:write-rejected` every guarded mutator emits, so the shell's
   * one listener names the reason — when the write must not happen.
   * @param {string} operation user-facing label of what was refused
   */
  refuseIfReadOnly(operation) {
    if (this.isWritable()) return false;
    this._rejectReadOnlyWrite(operation);
    return true;
  }

  _idbHandle() {
    if (!this._idb) {
      this._idb = createIdbHandle({
        name: IDB_NAME,
        version: IDB_VERSION,
        idbFactory: this._idbFactory || undefined,
        upgrade: (db) => {
          if (!db.objectStoreNames.contains(IDB_STORE)) {
            db.createObjectStore(IDB_STORE, { keyPath: 'key' });
          }
        }
      });
    }
    return this._idb;
  }

  getState() {
    return this.state;
  }

  _rejectReadOnlyWrite(operation) {
    eventBus.emit('store:write-rejected', { operation });
    return false;
  }

  /** CE-4. Install/read the path-scoped write policy. Null = allow-all. */
  setWritePolicy(policy) {
    this._writePolicy = policy || null;
  }

  getWritePolicy() {
    return this._writePolicy;
  }

  /**
   * True (and emits the rejection) when the installed policy refuses any of
   * `paths`. With no policy this is free and always false — the builder path.
   */
  _policyRejects(paths, operation) {
    const policy = this._writePolicy;
    if (!policy) return false;
    for (const path of paths) {
      if (!policy.allowsPath(path)) {
        eventBus.emit('store:write-rejected', { operation, policy: policy.reason, path });
        return true;
      }
    }
    return false;
  }

  /** True (and emits) when the policy forbids structural array ops. */
  _policyRejectsStructural(operation) {
    const policy = this._writePolicy;
    if (!policy || policy.allowsStructural) return false;
    eventBus.emit('store:write-rejected', { operation, policy: policy.reason, structural: true });
    return true;
  }

  init(state) {
    this.state = state || createDefaultState();
    normalizeStateLogoSlots(this.state);
    // EA-1. The one client-side seam, same archetype as the logo slots above:
    // `entrance` is HASHED content, so a live state that lacks it diverges from
    // a collab room that has materialized it, and the peer resync-adopts on the
    // next rejection instead of rolling back. Every production init reaches here
    // — load, collab snapshot adoption, agent adoption, a composed mockup — so
    // canonicalizing at this one point is what makes that impossible.
    normalizeEntranceState(this.state);
    // SC-1: same archetype — `schedule` is HASHED content, so every init
    // canonicalizes it (invalid shapes deleted, shared sections stripped) or a
    // live state diverges from a collab room that normalized it.
    normalizeScheduleState(this.state);
    normalizeEditableIdRepairState(this.state);
    this._revision = 0;
    this._epoch += 1;
    this._dirty = false;
    this._dirtyVersion = 0;
    this._saveToken += 1;
    clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = null;
    this._txn = null;
    this.history.clear();
    this.notify();
    this._emitHistoryChanged();
  }

  _bumpRevision() {
    this._revision = (Number.isFinite(this._revision) ? this._revision : 0) + 1;
    this._epoch += 1;
    if (this.state) this.state.updatedAt = Date.now();
    return this._revision;
  }

  update(path, newValue, actionLabel) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(actionLabel || 'update');
    if (this._policyRejects([path], actionLabel || 'update')) return false;
    newValue = normalizeLogoSlotsInValue(newValue, path, this.state);
    const oldValue = getByPath(this.state, path);
    if (valuesEqual(oldValue, newValue)) return false;
    setByPath(this.state, path, newValue);
    this._bumpRevision();

    if (this._txn) {
      this._txn.patches.push({ path, oldValue, newValue });
      // During a transaction: notify listeners (for live UI updates) but do NOT
      // emit history-changed (history hasn't changed yet — commit does that).
      // Also skip auto-save until commit.
      this.notify();
    } else {
      this._pushHistory(
        [{ op: 'replace', path, value: newValue }],
        [{ op: 'replace', path, value: oldValue }],
        actionLabel
      );
      this.notify();
      this._emitHistoryChanged();
      this.scheduleAutoSave();
    }
    return true;
  }

  batch(mutations, actionLabel) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(actionLabel || 'batch update');
    if (!Array.isArray(mutations) || mutations.length === 0) return;
    if (
      this._policyRejects(
        mutations.map((m) => m?.path),
        actionLabel || 'batch update'
      )
    ) {
      return false;
    }
    const forward = [];
    const reverse = [];
    for (const { path, value } of mutations) {
      const nextValue = normalizeLogoSlotsInValue(value, path, this.state);
      const old = getByPath(this.state, path);
      if (valuesEqual(old, nextValue)) continue;
      setByPath(this.state, path, nextValue);
      forward.push({ op: 'replace', path, value: nextValue });
      reverse.push({ op: 'replace', path, value: old });
    }
    if (forward.length === 0) return false;
    this._bumpRevision();
    this._pushHistory(forward, reverse.reverse(), actionLabel);
    this.notify();
    this._emitHistoryChanged();
    this.scheduleAutoSave();
    return true;
  }

  /**
   * Transaction-aware sibling of batch() (16B1): mutate N paths and notify
   * ONCE. update() calls notify() on EVERY call including inside an open
   * transaction, so a 2s scrub across 6 sections is 120 frames × 6 = 720
   * state-changed emissions, each walking every subscriber; updateMany makes
   * it 120. Duplicate paths within one call normalize to a single write (last
   * value wins). Inside a transaction the writes land as transaction patches
   * (coalesced at commit); outside one it behaves exactly as a single batch —
   * one history entry, one notify, one autosave.
   * @param {Array<{path: string, value: *}>} mutations
   * @returns {boolean} whether anything actually changed
   */
  updateMany(mutations, actionLabel) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(actionLabel || 'update many');
    if (!Array.isArray(mutations) || mutations.length === 0) return false;
    if (
      this._policyRejects(
        mutations.filter((m) => m && typeof m.path === 'string').map((m) => m.path),
        actionLabel || 'update many'
      )
    ) {
      return false;
    }
    const byPath = new Map();
    for (const mutation of mutations) {
      if (!mutation || typeof mutation.path !== 'string') continue;
      byPath.set(mutation.path, mutation.value);
    }
    const applied = [];
    for (const [path, rawValue] of byPath) {
      const value = normalizeLogoSlotsInValue(rawValue, path, this.state);
      const oldValue = getByPath(this.state, path);
      if (valuesEqual(oldValue, value)) continue;
      setByPath(this.state, path, value);
      applied.push({ path, oldValue, newValue: value });
    }
    if (applied.length === 0) return false;
    this._bumpRevision();
    if (this._txn) {
      this._txn.patches.push(...applied);
      this.notify(); // once — this is the entire point of the method
    } else {
      this._pushHistory(
        applied.map((p) => ({ op: 'replace', path: p.path, value: p.newValue })),
        applied.map((p) => ({ op: 'replace', path: p.path, value: p.oldValue })).reverse(),
        actionLabel
      );
      this.notify();
      this._emitHistoryChanged();
      this.scheduleAutoSave();
    }
    return true;
  }

  insertAt(arrayPath, index, item, actionLabel) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(actionLabel || 'insert');
    if (this._policyRejectsStructural(actionLabel || 'insert')) return false;
    const arr = getByPath(this.state, arrayPath);
    const clone = structuredClone(item);
    normalizeSectionLogoSlots(clone);
    arr.splice(index, 0, clone);
    this._bumpRevision();
    this._pushHistory(
      [{ op: 'insert', path: arrayPath, index, item: structuredClone(clone) }],
      [{ op: 'remove', path: arrayPath, index }],
      actionLabel
    );
    this.notify();
    this._emitHistoryChanged();
    this.scheduleAutoSave();
    return true;
  }

  removeAt(arrayPath, index, actionLabel) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(actionLabel || 'remove');
    if (this._policyRejectsStructural(actionLabel || 'remove')) return false;
    const arr = getByPath(this.state, arrayPath);
    const removed = arr.splice(index, 1)[0];
    this._bumpRevision();
    this._pushHistory(
      [{ op: 'remove', path: arrayPath, index }],
      [{ op: 'insert', path: arrayPath, index, item: structuredClone(removed) }],
      actionLabel
    );
    this.notify();
    this._emitHistoryChanged();
    this.scheduleAutoSave();
    return true;
  }

  moveInArray(arrayPath, fromIndex, toIndex, actionLabel) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(actionLabel || 'move');
    if (this._policyRejectsStructural(actionLabel || 'move')) return false;
    const arr = getByPath(this.state, arrayPath);
    const [item] = arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, item);
    this._bumpRevision();
    this._pushHistory(
      [{ op: 'move', path: arrayPath, from: fromIndex, to: toIndex }],
      [{ op: 'move', path: arrayPath, from: toIndex, to: fromIndex }],
      actionLabel
    );
    this.notify();
    this._emitHistoryChanged();
    this.scheduleAutoSave();
    return true;
  }

  /**
   * Current mutation revision — the concurrency token for applyCompound's
   * expectedRevision. Unlike getEpoch() it is what _bumpRevision maintains for
   * history semantics; capture it AFTER the last await in a flow and pass it
   * straight through so a mutation that lands in between is detected.
   */
  getRevision() {
    return this._revision;
  }

  /**
   * Apply a compound structural change atomically (Polish 16A2b). Validates
   * EVERYTHING before touching state: writable, no open transaction, expected
   * revision, patch shape, and — by simulating on a clone — that `forward`
   * applies cleanly AND that `reverse` exactly inverts it (the aliasing bug
   * class from constraint C3 fails here instead of corrupting history). On
   * success: applies, records ONE history entry (patches cloned at the
   * boundary), notifies once, emits the collab op once (via the _pushHistory
   * choke point), and schedules one autosave. Returns false with ZERO side
   * effects on any validation failure.
   *
   * @param {{forward: object[], reverse: object[], label: string,
   *   expectedRevision?: number, options?: {preserveFuture?: boolean}}} compound
   */
  applyCompound({ forward, reverse, label, expectedRevision, options = {} } = {}) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(label || 'compound update');
    if (this._writePolicy) {
      // CE-4: a compound is structural whenever any op is not a plain replace;
      // replace paths are policy-checked like any other write.
      const structural = [...(forward || []), ...(reverse || [])].some(
        (p) => p && p.op !== 'replace'
      );
      if (structural && this._policyRejectsStructural(label || 'compound update')) return false;
      if (
        this._policyRejects(
          (forward || []).filter((p) => p?.op === 'replace').map((p) => p.path),
          label || 'compound update'
        )
      ) {
        return false;
      }
    }
    if (this._txn) return false;
    if (expectedRevision != null && expectedRevision !== this._revision) return false;
    if (!Array.isArray(forward) || forward.length === 0) return false;
    if (!Array.isArray(reverse) || reverse.length === 0) return false;
    const validOps = new Set(['replace', 'insert', 'remove', 'move']);
    for (const patch of [...forward, ...reverse]) {
      if (!patch || typeof patch.path !== 'string' || !validOps.has(patch.op)) return false;
      if (
        Object.prototype.hasOwnProperty.call(patch, 'absent') &&
        !isEditableIdRepairAbsentPatch(patch)
      )
        return false;
      if (patch.op === 'insert' || patch.op === 'remove') {
        if (!Number.isInteger(patch.index) || patch.index < 0) return false;
      }
      if (patch.op === 'move') {
        if (!Number.isInteger(patch.from) || patch.from < 0) return false;
        if (!Number.isInteger(patch.to) || patch.to < 0) return false;
      }
    }
    // Simulate forward + reverse on a clone. Array ops must resolve to a real
    // array with in-range indices (applyPatches clamps silently — a stale
    // index would delete the WRONG section, which is exactly what the
    // validate-await-revalidate contract exists to prevent).
    const simulation = structuredClone(this.state);
    const applySim = (patches) => {
      for (const patch of patches) {
        if (patch.op !== 'replace') {
          const arr = getByPath(simulation, patch.path);
          if (!Array.isArray(arr)) return false;
          const max = patch.op === 'insert' ? arr.length : arr.length - 1;
          if (patch.op === 'move') {
            if (patch.from > arr.length - 1 || patch.to > arr.length - 1) return false;
          } else if (patch.index > max) {
            return false;
          }
        }
        try {
          applyPatches(simulation, [structuredClone(patch)]);
        } catch {
          return false;
        }
      }
      return true;
    };
    if (!applySim(forward)) return false;
    // New malformed evidence cannot be committed. Existing unsupported bytes
    // remain recoverable when an unrelated compound operation leaves them alone.
    for (const { path, section } of editableIdRepairSections(simulation)) {
      const normalized = normalizeEditableIdRepairs(section.editableIdRepairs, {
        instanceId: section.instanceId
      });
      if (
        (!normalized.ok || normalized.value === undefined) &&
        !valuesEqual(section.editableIdRepairs, getByPath(this.state, `${path}.editableIdRepairs`))
      )
        return false;
    }
    if (!applySim(reverse)) return false;
    if (!valuesEqual(simulation, this.state)) return false; // reverse must invert forward

    const committedForward = structuredClone(forward);
    const committedReverse = structuredClone(reverse);
    applyPatches(this.state, committedForward);
    this._bumpRevision();
    // _pushHistory clones at the boundary (C3), so passing these through does
    // not let the caller's arrays alias the stored entry.
    this._pushHistory(committedForward, committedReverse, label, {
      preserveFuture: options.preserveFuture === true,
      identityRepairContext: options.identityRepairContext
    });
    this.notify();
    this._emitHistoryChanged();
    this.scheduleAutoSave();
    return true;
  }

  beginTransaction(label) {
    if (!this.isWritable()) return this._rejectReadOnlyWrite(label || 'begin transaction');
    this._txn = {
      label,
      snapshot: structuredClone(this.state),
      patches: []
    };
    return true;
  }

  commitTransaction(options = {}) {
    if (!this._txn) return false;
    if (!this.isWritable()) {
      this.abortTransaction();
      return this._rejectReadOnlyWrite('commit transaction');
    }
    const { label, patches, snapshot } = this._txn;
    if (patches.length === 0) {
      this._txn = null;
      return false;
    }
    // 16B1: merge repeated-path writes (first oldValue, last newValue), keep
    // raw order across intersecting paths, drop net no-ops. A scrub returned
    // to its starting value commits NOTHING — no entry, no collab frame.
    const coalesced = coalesceTransactionPatches(patches);
    if (coalesced.length === 0) {
      this._txn = null;
      // Listeners still get the settle notification the commit boundary has
      // always provided; history, collab and autosave stay untouched.
      this.notify();
      return false;
    }
    const mutationPatches = coalesced.map((p) => ({
      op: 'replace',
      path: p.path,
      oldValue: p.oldValue,
      newValue: p.newValue
    }));
    this._bumpRevision();
    this._pushHistory(
      coalesced.map((p) => ({ op: 'replace', path: p.path, value: p.newValue })),
      coalesced.map((p) => ({ op: 'replace', path: p.path, value: p.oldValue })).reverse(),
      label,
      { preserveFuture: options.preserveFuture === true }
    );
    this._txn = null;
    // Emit one final state notification at commit boundary so listeners that
    // depend on the settled end-of-transaction snapshot can synchronize.
    this.notify();
    this._emitHistoryChanged('mutation', mutationPatches, snapshot, this.state);
    this.scheduleAutoSave();
    return true;
  }

  isInTransaction() {
    return !!this._txn;
  }

  /**
   * Fold a follow-up write into the most recent committed history entry
   * (Panel Readback Phase 6 apply-and-verify escalation). Applies `newValue`
   * at `path` WITHOUT creating a new history entry: the entry's forward patch
   * for that path is updated (or appended, with a matching reverse patch) so
   * redo restores the amended value while one undo still reverts the whole
   * original write + amendment. Refuses (returns false) when:
   *  - the store is read-only or a transaction is open, or
   *  - `entry` is no longer the latest committed entry (history moved on,
   *    was undone, or was never committed) — the caller must NOT fall back to
   *    a plain update(), which would mint the second entry this API exists
   *    to avoid.
   */
  amendLastEntry(entry, path, newValue) {
    if (!this.isWritable() || this._txn) return false;
    const latest = this.history.entries[this.history.position];
    if (!entry || latest !== entry) return false;
    if (!Array.isArray(entry.forward) || !Array.isArray(entry.reverse)) return false;
    const oldValue = getByPath(this.state, path);
    if (valuesEqual(oldValue, newValue)) return true;
    setByPath(this.state, path, newValue);
    this._bumpRevision();
    const fwd = entry.forward.filter((p) => p && p.path === path).pop();
    if (fwd) {
      fwd.value = structuredClone(newValue);
    } else {
      entry.forward.push({ op: 'replace', path, value: structuredClone(newValue) });
      // Reverse patches run in reverse-write order; restoring the pre-amend
      // value first keeps the original patches' expectations intact.
      entry.reverse.unshift({ op: 'replace', path, value: structuredClone(oldValue) });
    }
    this.notify();
    this._emitCollabOp(
      [{ op: 'replace', path, value: structuredClone(newValue) }],
      [{ op: 'replace', path, value: structuredClone(oldValue) }],
      entry.label,
      'amend'
    );
    this.scheduleAutoSave();
    return true;
  }

  abortTransaction() {
    if (!this._txn) return;
    const hadPatches = Array.isArray(this._txn.patches) && this._txn.patches.length > 0;
    this.state = this._txn.snapshot;
    this._txn = null;
    if (!hadPatches) return;
    if (hadPatches) this._bumpRevision();
    this.notify();
  }

  undo() {
    if (!this.isWritable()) return this._rejectReadOnlyWrite('undo');
    // An open transaction is the most-recent change, so one Cmd+Z must revert
    // exactly it — not it PLUS the previous committed entry (the old abort-then-
    // history.undo double revert). Commit the in-progress edit as a real entry so
    // history.undo below reverts just that one step and redo can restore it. An
    // empty transaction (bare focus, no edits) is discarded so undo falls through
    // to the previous committed entry.
    if (this._txn) {
      const hadPatches = Array.isArray(this._txn.patches) && this._txn.patches.length > 0;
      if (hadPatches) this.commitTransaction();
      else this.abortTransaction();
    }
    const previousState = structuredClone(this.state);
    const entry = this.history.canUndo ? this.history.entries[this.history.position] : null;
    if (!this._allowsIdentityRepairHistory(entry, 'undo')) return false;
    // CE-4 defensive: only allowed writes ever enter history under a policy,
    // so this can fire only on an entry that predates the policy install.
    if (
      entry &&
      this._policyRejects(
        (entry.reverse || []).map((p) => p.path),
        'undo'
      )
    ) {
      return false;
    }
    if (this.history.undo(this.state)) {
      this._bumpRevision();
      this.notify();
      this._emitHistoryChanged('undo', entry?.reverse || null, previousState, this.state);
      // Collab: an undo IS a mutation to peers — its wire op is the entry's
      // reverse (and redo would re-send the forward).
      if (entry) this._emitCollabOp(entry.reverse, entry.forward, entry.label, 'undo');
      this.scheduleAutoSave();
      return true;
    }
    return false;
  }

  redo() {
    if (!this.isWritable()) return this._rejectReadOnlyWrite('redo');
    if (this._txn) return false;
    const previousState = structuredClone(this.state);
    const nextEntry = this.history.canRedo ? this.history.entries[this.history.position + 1] : null;
    if (!this._allowsIdentityRepairHistory(nextEntry, 'redo')) return false;
    if (
      nextEntry &&
      this._policyRejects(
        (nextEntry.forward || []).map((p) => p.path),
        'redo'
      )
    ) {
      return false;
    }
    if (this.history.redo(this.state)) {
      this._bumpRevision();
      this.notify();
      this._emitHistoryChanged('redo', nextEntry?.forward || null, previousState, this.state);
      if (nextEntry)
        this._emitCollabOp(nextEntry.forward, nextEntry.reverse, nextEntry.label, 'redo');
      this.scheduleAutoSave();
      return true;
    }
    return false;
  }

  get historyPosition() {
    return this.history.position;
  }

  /** Store a UI preference without polluting undo history. */
  setPreference(key, value) {
    if (!this.state) return;
    if (!this.isWritable()) return this._rejectReadOnlyWrite('update preference');
    if (!this.state.preferences) this.state.preferences = {};
    if (this.state.preferences[key] === value) return;
    this.state.preferences[key] = value;
    this.notify();
    this.scheduleAutoSave();
  }

  /**
   * Internal cache-fill mutation. Used by lazy page hydration to write
   * fetched section instances without polluting undo history or marking
   * the project as user-modified.
   *
   * Does NOT push to history, call _bumpRevision (which updates updatedAt),
   * or schedule autosave. Background prefetch schedules one explicit autosave
   * on completion — see section-manager startBackgroundPrefetch().
   *
   * Does setByPath + notify so UI subscribers (e.g. page-tabs strip) can
   * rerender to drop a "loading" badge once a page becomes hydrated.
   */
  updateSilent(path, value) {
    if (!this.state) return;
    if (!this.isWritable()) return this._rejectReadOnlyWrite('silent update');
    if (this._policyRejects([path], 'silent update')) return;
    setByPath(this.state, path, normalizeLogoSlotsInValue(value, path, this.state));
    this.notify();
  }

  /**
   * Apply a REMOTE collaborator's patches (Collab Phase 7). First-class store
   * method — NOT updateSilent: it bumps `_epoch` AND `_revision` (epoch races
   * and render-staleness guards stay correct), stamps updatedAt (via
   * _bumpRevision), schedules autosave, pushes NO history entry, and emits
   * `collab:remote-applied` (never `history-changed`).
   *
   * Patch paths/order apply verbatim, but section HTML gets the Motion M4
   * residue backstop before application. This mirrors the room core's
   * authoritative normalization and deliberately does NOT run the logo
   * normalizer, preserving the byte contract for unrelated remote markup. When
   * a transaction is open, a clone of the patches is dual-applied to the txn
   * snapshot so Esc-cancel cannot revert remote ops (commit diffs from
   * _txn.patches — unaffected).
   *
   * If a remote op removes the page this client is viewing, activePage is
   * repaired locally (view-level; never replicated — the repair happens
   * outside any capture path).
   */
  applyRemoteOps(patches, { authorId = null, label = null, seq = null } = {}) {
    if (!this.state) return false;
    // A room follower is locally read-only but must still converge on the
    // authoritative writer's operations. Every other blocker (tab ownership,
    // auth, unavailable project) remains a hard stop.
    const remoteOnlyBlocked = this._blockers.has(WRITE_BLOCKER.SESSION_CLAIM)
      ? [...this._blockers].every((blocker) => blocker === WRITE_BLOCKER.SESSION_CLAIM)
      : false;
    if (!this.isWritable() && !remoteOnlyBlocked) return this._rejectReadOnlyWrite('remote op');
    // Defensive inbound strip — peers pre-strip, but a hostile/buggy peer's
    // activePage write must never yank this client's view.
    const applied = normalizeRemoteMotionPatches(
      this.state,
      stripLocalOnlyPatches(Array.isArray(patches) ? patches : [])
    );
    if (!applied.length) return false;
    this._applyingRemote = true;
    try {
      applyPatches(this.state, applied);
      normalizeEditableIdRepairState(this.state);
      if (this._txn?.snapshot) {
        try {
          applyPatches(this._txn.snapshot, structuredClone(applied));
        } catch {
          // Snapshot drifted from the live shape; abort would now lose the
          // remote values — drop the snapshot patches rather than corrupt it.
        }
      }
    } catch (err) {
      this._applyingRemote = false;
      reportError('StateStore remote apply', err, {
        user: 'silent',
        detail: `remote op seq ${seq ?? '?'} failed to apply`
      });
      return false;
    }
    this._applyingRemote = false;
    if (
      Array.isArray(this.state.pages) &&
      !this.state.pages.some((page) => page?.id === this.state.activePage)
    ) {
      this.state.activePage = this.state.pages[0]?.id ?? null;
    }
    this._bumpRevision();
    this.notify();
    this.scheduleAutoSave();
    eventBus.emit('collab:remote-applied', { patches: applied, authorId, label, seq });
    return true;
  }

  /**
   * Collab Phase 9: conservative undo cut-below truncation. After a remote op
   * (or a rejection rollback) touches paths a local history entry also touches,
   * undoing past that entry would resurrect a value the room no longer agrees
   * with — so those entries are retired. Emits a render-inert 'collab-trim'
   * history-changed so undo/redo affordances refresh without repainting.
   */
  trimHistoryForRemote(patches) {
    if (!Array.isArray(patches) || patches.length === 0) return false;
    const trimmed = trimHistoryAgainst(this.history, patches);
    if (trimmed) this._emitHistoryChanged('collab-trim');
    return trimmed;
  }

  getPreference(key) {
    return this.state?.preferences?.[key] ?? null;
  }

  _emitHistoryChanged(source = 'mutation', patches = null, oldState = null, newState = null) {
    let label = '';
    const entries = this.history.entries;
    const pos = this.history.position;
    if (source === 'undo' && pos + 1 < entries.length) {
      label = entries[pos + 1].label || '';
    } else if (source === 'redo' && pos >= 0 && pos < entries.length) {
      label = entries[pos].label || '';
    } else if (source === 'mutation' && pos >= 0 && pos < entries.length) {
      label = entries[pos].label || '';
    }
    eventBus.emit('history-changed', {
      canUndo: this.history.canUndo,
      canRedo: this.history.canRedo,
      source,
      label,
      patches: Array.isArray(patches) ? patches : null,
      oldState,
      newState
    });
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  notify() {
    for (const fn of this.listeners) {
      try {
        fn(this.state);
      } catch (err) {
        console.error('[StateStore] Error in listener:', err);
      }
    }
    eventBus.emit('state-changed', this.state);
  }

  scheduleAutoSave() {
    const wasDirty = this._dirty;
    this._dirty = true;
    this._dirtyVersion += 1;
    // Emit once per clean→dirty cycle so the toolbar chip can show "Unsaved
    // changes" without event spam on every keystroke (it re-clears on save).
    if (!wasDirty) eventBus.emit('autosave:dirty');
    if (!this.isWritable()) return;
    // CE-4: a token session persists nothing locally — the dirty chip above
    // still works, and the guest's save path is the explicit Save button.
    if (this._writePolicy && this._writePolicy.allowsLocalPersistence === false) return;
    clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = setTimeout(() => this.saveToIDB(), AUTOSAVE_DELAY);
  }

  isDirty() {
    return this._dirty;
  }

  /**
   * Monotonic load/mutation epoch — increments on every mutation and every
   * init(), never resets, never repeats. Callers that await async work (e.g.
   * downloading a cloud bundle) capture getEpoch() before the await and compare
   * after: an unequal value means the project changed underneath them, so the
   * stale result must not be applied. More reliable than millisecond updatedAt
   * timestamps, which collide on same-tick edits.
   */
  getEpoch() {
    return this._epoch;
  }

  isWritable() {
    return this._blockers.size === 0;
  }

  /**
   * Read-only reasons, highest priority first. The order decides which one a
   * user-facing message names when several are held at once.
   */
  static get BLOCKERS() {
    return WRITE_BLOCKERS;
  }

  /**
   * Add a write blocker. Writability is the EMPTINESS of the blocker set, so
   * several independent gates (tab ownership, project lifecycle, auth, a server
   * session claim) can be held at once and releasing one can never re-enable a
   * project another still blocks. That composition is the point: a single
   * boolean made "the last gate to release wins", which is a silent-data-loss
   * bug waiting for two overlapping gates.
   *
   * NON-DESTRUCTIVE by default. Discarding unsaved work is a separate, explicit
   * decision — see `discardUnsavedForBlocker`. The tab-guard genuinely wants the
   * discard (another tab owns the shared slot and will write it); the lifecycle
   * blocker must NEVER discard, because preserving the user's work so they can
   * save a copy is the entire point of showing them the banner.
   *
   * @param {string} blocker one of StateStore.BLOCKERS
   * @returns {boolean} true when this call changed writability
   */
  addBlocker(blocker) {
    const id = String(blocker || '');
    if (!VALID_BLOCKERS.has(id)) throw new Error(`Unknown write blocker: ${id}`);
    if (this._blockers.has(id)) return false;
    const wasWritable = this.isWritable();
    this._blockers.add(id);
    if (wasWritable) {
      eventBus.emit('writability-changed', {
        writable: false,
        blocker: id,
        blockers: this.getBlockers()
      });
    }
    return wasWritable;
  }

  /**
   * Release one blocker. Writability returns only when the set empties.
   * @param {string} blocker
   * @returns {boolean} true when this call restored writability
   */
  removeBlocker(blocker) {
    const id = String(blocker || '');
    if (!this._blockers.delete(id)) return false;
    const writable = this.isWritable();
    if (writable) {
      eventBus.emit('writability-changed', { writable: true, blocker: id, blockers: [] });
    }
    return writable;
  }

  /** True when the named blocker is currently held. */
  hasBlocker(blocker) {
    return this._blockers.has(String(blocker || ''));
  }

  /** Held blockers, highest priority first. Copy — callers cannot mutate state. */
  getBlockers() {
    return WRITE_BLOCKERS.filter((id) => this._blockers.has(id));
  }

  /**
   * The blocker a user-facing message should name, or null when writable.
   * Priority order, not insertion order: telling someone "another tab is
   * editing" when their project was actually deleted is the more misleading of
   * the two facts.
   */
  getPrimaryBlocker() {
    return this.getBlockers()[0] || null;
  }

  /**
   * Drop unsaved local state, explicitly. This is what `setWritable(false)` used
   * to do implicitly for every caller. It is correct ONLY when another writer
   * owns the persistence slot and will write it — i.e. the tab-guard handing off.
   * Never call it for a blocker whose whole purpose is to preserve the work.
   */
  discardUnsavedForBlocker() {
    clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = null;
    if (this._txn) this.abortTransaction();
    this._dirty = false;
    this._saveToken += 1;
  }

  /**
   * Legacy boolean gate, kept so the ~30 existing `store.setWritable(...)` and
   * `store.isWritable()` call sites are untouched by SB-0. It maps onto the
   * TAB_GUARD blocker and preserves the historical discard-on-false behavior
   * exactly — the tab-guard is the caller that wants it.
   *
   * New code should use addBlocker/removeBlocker with a specific reason.
   * @param {boolean} writable
   */
  setWritable(writable) {
    if (writable) {
      this.removeBlocker(WRITE_BLOCKER.TAB_GUARD);
      return;
    }
    const wasWritable = this.isWritable();
    this.addBlocker(WRITE_BLOCKER.TAB_GUARD);
    // Only on the writable -> read-only edge, matching the old `if (changed)`
    // guard: a repeated setWritable(false) must not re-abort a transaction.
    if (wasWritable) this.discardUnsavedForBlocker();
  }

  /**
   * Immediately persist pending mutations, bypassing the autosave debounce.
   * No-op when clean or when this tab is read-only. Safe to call repeatedly —
   * the save-token guard in saveToIDB() handles overlapping calls.
   */
  flushAutoSave() {
    if (!this._dirty || !this.isWritable()) return Promise.resolve();
    clearTimeout(this._autoSaveTimer);
    this._autoSaveTimer = null;
    return this.saveToIDB();
  }

  async saveToIDB() {
    // Defense in depth: a read-only tab must never touch the shared slot even if
    // a stale timer or a direct call slips through the scheduleAutoSave gate.
    if (!this.isWritable()) return;
    if (this._writePolicy && this._writePolicy.allowsLocalPersistence === false) return;
    const token = ++this._saveToken;
    const dirtyVersionAtStart = this._dirtyVersion;
    eventBus.emit('autosave:saving', { token });
    const delays = this._autosaveRetryDelays;
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        await this._writeAutosaveRecord(token);
        eventBus.emit('auto-saved');
        if (token === this._saveToken && dirtyVersionAtStart === this._dirtyVersion) {
          this._dirty = false;
          eventBus.emit('autosave:saved', { token });
        }
        return;
      } catch (err) {
        // A newer save owns retrying and error reporting from here on.
        if (token !== this._saveToken) return;
        if (attempt < delays.length) {
          console.warn('[StateStore] Auto-save failed, retrying:', err);
          await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
          if (token !== this._saveToken) return;
          eventBus.emit('autosave:saving', { token });
          continue;
        }
        console.error('[StateStore] Auto-save failed:', err);
        const reason = (err?.message || '').slice(0, 80);
        // Route terminal failure through the central reporter (silent — the
        // toolbar chip already surfaces autosave:error to the user).
        reportError('Autosave', err, { user: 'silent', detail: reason });
        eventBus.emit('autosave:error', { token, reason });
      }
    }
  }

  /**
   * The account this tab's slots belong to. Set by the shell once auth resolves
   * and on every sign-in/sign-out; `local` until then, which is also the
   * signed-out and dev value.
   */
  setPrincipalScope(scope) {
    this._principalScope = String(scope || LOCAL_PRINCIPAL_SCOPE);
  }

  getPrincipalScope() {
    return this._principalScope || LOCAL_PRINCIPAL_SCOPE;
  }

  /**
   * This tab's current slot key.
   *
   * A state with no `projectId` should not exist — `migrateState` assigns one to
   * every project on every load path — but if one ever reaches here, the save
   * falls back to the legacy global key rather than being DROPPED. Silently
   * writing nothing is the worse failure: it looks like a working autosave and
   * loses the session. Returns null only when there is no state at all.
   */
  autosaveKey() {
    if (!this.state) return null;
    const projectId = this.state.projectId;
    return projectId ? autosaveKeyFor(this.getPrincipalScope(), projectId) : LEGACY_AUTOSAVE_KEY;
  }

  async _writeAutosaveRecord(token) {
    // The callback is synchronous w.r.t. the tx, so this.state cannot mutate
    // between capture and put(); IDB structured-clones the record during put(),
    // so the redundant pre-clone is dropped.
    await withStore(this._idbHandle(), IDB_STORE, 'readwrite', (objStore) => {
      // Stale-token guard: init() (a new project load) bumps _saveToken. If that
      // happened while this tx was opening, this save belongs to a superseded
      // project — skip the put so an in-flight autosave of A can't commit over
      // B's slot after init(B). PRESERVED VERBATIM through SB-3: per-project
      // keys make the cross-project case impossible by construction, but this
      // also covers same-project supersession, which they do not.
      if (token !== undefined && token !== this._saveToken) return;
      // SB-3b is the CONTRACT half: only the scoped slot is written now. SB-3a
      // shipped the dual-write for one release so a rollback found readable
      // data; keeping it any longer would mean every save also rewrote a global
      // record that the next boot could fall back to, quietly resurrecting the
      // single-slot behavior this phase removes.
      // FN-5: the envelope carries its OWN ownership proof beside the scoped
      // key. The key alone cannot refuse — a legacy global record has no
      // account in it to check, and a key is a lookup, not evidence. With
      // `ownerSub` stamped at write time, recovery can positively match, and an
      // absent field is unambiguously "legacy, ownership unknown" rather than
      // "owned by nobody". `local` (LOCAL_PRINCIPAL_SCOPE) is a real value here,
      // not a null stand-in, for that same reason.
      const key = this.autosaveKey();
      if (key) {
        objStore.put({
          state: this.state,
          savedAt: Date.now(),
          key,
          ownerSub: this.getPrincipalScope()
        });
      }
    });
  }

  /**
   * Read this tab's slot, falling back to the legacy global slot.
   *
   * @param {{ projectId?: string, legacyFallback?: boolean }} [opts]
   *   `projectId` reads a specific project's slot instead of the loaded one —
   *   boot needs this because it must probe BEFORE a project is in the store.
   */
  async loadFromIDB({ projectId = null, legacyFallback = true } = {}) {
    const key = projectId
      ? autosaveKeyFor(this.getPrincipalScope(), projectId)
      : this.autosaveKey();
    try {
      // BOTH requests are issued before the first await. Issuing the second one
      // AFTER awaiting the first is the classic IndexedDB footgun: the
      // transaction auto-commits as soon as the microtask queue drains with no
      // pending request, so the follow-up get is made against a dead tx and
      // never settles. Costs one extra get on the common path; correctness wins.
      return await withStore(this._idbHandle(), IDB_STORE, 'readonly', async (objStore) => {
        const scopedReq = key ? promisifyRequest(objStore.get(key)) : Promise.resolve(null);
        const legacyReq = legacyFallback
          ? promisifyRequest(objStore.get(LEGACY_AUTOSAVE_KEY))
          : Promise.resolve(null);
        const [scoped, legacy] = await Promise.all([scopedReq, legacyReq]);
        // `ownerSub` is surfaced verbatim, INCLUDING when absent: an undefined
        // here means a record written before FN-5, which the caller must treat
        // as unknown ownership rather than as a mismatch. Defaulting it to the
        // current principal would silently adopt another account's work.
        if (scoped) {
          return {
            state: scoped.state,
            savedAt: scoped.savedAt,
            ownerSub: scoped.ownerSub,
            scoped: true
          };
        }
        return legacy
          ? {
              state: legacy.state,
              savedAt: legacy.savedAt,
              ownerSub: legacy.ownerSub,
              scoped: false
            }
          : null;
      });
    } catch (err) {
      console.error('[StateStore] Load from IDB failed:', err);
      return null;
    }
  }

  /**
   * Copy the legacy global slot into its own (principal, project) slot.
   *
   * Write-new-then-delete-old is the ONLY safe order, and SB-3a does not delete
   * at all: an interrupted migration must leave a readable slot, and a rollback
   * to pre-SB-3 code must still find one. SB-3b removes the legacy copy once the
   * new key has shipped for a release. Idempotent — a slot that is already
   * migrated is left alone rather than overwritten, so a newer scoped record is
   * never clobbered by an older legacy one.
   *
   * @returns {Promise<{migrated: boolean, key: string|null}>}
   */
  async migrateLegacyAutosaveSlot() {
    try {
      // ONE request per transaction. The scoped key cannot be known until the
      // legacy record has been read (it carries the project id), so these
      // cannot be issued together — and issuing the second after an await
      // inside one transaction would make it against an auto-committed tx.
      // Separate transactions are the correct shape; the write is
      // write-if-absent and idempotent, so two tabs racing this at boot both
      // converge on the same bytes.
      const legacy = await withStore(this._idbHandle(), IDB_STORE, 'readonly', (objStore) =>
        promisifyRequest(objStore.get(LEGACY_AUTOSAVE_KEY))
      );
      const projectId = legacy?.state?.projectId;
      if (!projectId) {
        recordSessionBoundaryMetric('autosave_slot_migration', { outcome: 'not_needed' });
        return { migrated: false, key: null };
      }
      const principal = this.getPrincipalScope();

      // FN-5 CORRECTION: provenance is VALIDATED before migration, never
      // inferred from the destination key.
      //
      // The first version of this stamped the record with whichever principal
      // happened to be signing in and then deleted the original — so account B
      // booting on a shared machine took ownership of account A's work, and the
      // recovery check downstream then saw a record that agreed with B. A key
      // is where a record is filed; it is not evidence of whose it is.
      const legacyOwner = legacy.ownerSub;
      if (legacyOwner !== undefined && legacyOwner !== principal) {
        // Someone else's. Do not copy it, and above all do not delete it — that
        // would destroy their only local copy on the way past.
        recordSessionBoundaryMetric('autosave_slot_migration', { outcome: 'refused_foreign' });
        return { migrated: false, key: null, refused: 'owner-mismatch' };
      }

      const key = autosaveKeyFor(principal, projectId);
      const existing = await withStore(this._idbHandle(), IDB_STORE, 'readonly', (objStore) =>
        promisifyRequest(objStore.get(key))
      );
      // Never clobber a NEWER scoped record with an older legacy one.
      if (!existing) {
        await withStore(this._idbHandle(), IDB_STORE, 'readwrite', (objStore) => {
          const record = { key, state: legacy.state, savedAt: legacy.savedAt };
          // An owner is carried across, never invented. A record that arrived
          // without one keeps that ambiguity into its scoped slot, where
          // `checkAutoSave` reads an absent owner as "unknown, still
          // recoverable" rather than as a match.
          if (legacyOwner !== undefined) record.ownerSub = legacyOwner;
          objStore.put(record);
        });
      }
      // SB-3b still retires the global record, and that survives FN-5 for a
      // reason worth stating: the legacy key is the ONE slot `loadFromIDB`
      // falls back to for ANY principal, so a record left there is offered to
      // every account that boots afterwards. Preserving an ambiguous original
      // would reopen exactly the cross-account exposure SB-3b closed.
      //
      // What FN-5 removes is the FABRICATION, not the retirement: the copy above
      // carries no invented owner, so `checkAutoSave` reads it as "unknown, still
      // recoverable" rather than as a positive match, and the scoped key means
      // only the principal who migrated it can reach it at all. A record owned
      // by someone ELSE never gets here — it was refused above, and preserved,
      // which is safe precisely because its owner is recorded.
      await withStore(this._idbHandle(), IDB_STORE, 'readwrite', (objStore) => {
        objStore.delete(LEGACY_AUTOSAVE_KEY);
      });
      recordSessionBoundaryMetric('autosave_slot_migration', {
        outcome: existing
          ? 'already_scoped'
          : legacyOwner === undefined
            ? 'migrated_unattributed'
            : 'migrated'
      });
      return { migrated: !existing, key, legacyRetired: true };
    } catch (err) {
      console.error('[StateStore] Autosave slot migration failed:', err);
      recordSessionBoundaryMetric('autosave_slot_migration', { outcome: 'failed' });
      return { migrated: false, key: null };
    }
  }

  async clearIDB({ projectId = null, legacy = true } = {}) {
    const key = projectId
      ? autosaveKeyFor(this.getPrincipalScope(), projectId)
      : this.autosaveKey();
    try {
      await withStore(this._idbHandle(), IDB_STORE, 'readwrite', (objStore) => {
        if (key) objStore.delete(key);
        // "Start fresh" must clear BOTH while the legacy slot is still written,
        // or the next boot's legacy fallback resurrects what was just discarded.
        if (legacy) objStore.delete(LEGACY_AUTOSAVE_KEY);
      });
    } catch (err) {
      console.error('[StateStore] Clear IDB failed:', err);
    }
  }

  getActivePage() {
    if (!this.state) return null;
    return this.state.pages.find((p) => p.id === this.state.activePage) || this.state.pages[0];
  }
}

export const store = new StateStore();
